/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable no-param-reassign */
/* eslint-disable react/destructuring-assignment */
import React, { useMemo, useCallback, useRef } from 'react'
import type {
  ColDef,
  GridOptions,
  CellSelectionOptions,
  CellValueChangedEvent,
  ValueGetterParams,
  ValueSetterParams,
  EditableCallbackParams
} from 'ag-grid-enterprise'
import { useTranslation } from 'react-i18next'

import CustomAgGrid from '../../../shared/components/custom/Grid/CustomAgGrid'
import { formatGridValue } from '../../../shared/components/custom/Grid/columnTypes'
import { AllocationBudgetRow, AllocationMethod } from '../types'
import { useCtxDimensions } from '../../dimension/queries/useCtxDimensions'
import { dimensionMapSelector } from '../../dimension/selectors/selectors'

interface BudgetSaveTableProps {
  rows: AllocationBudgetRow[]
  onMethodChange: (rowId: string, method: AllocationMethod) => void
  onOptionsChange: (rowId: string, options: AllocationBudgetRow['allocationOptions']) => void
}

export const BudgetAllocationTable: React.FC<BudgetSaveTableProps> = ({ rows, onMethodChange, onOptionsChange }) => {
  const { t } = useTranslation()
  const gridRef = useRef<any>(null)
  const modalContentRef = useRef<HTMLDivElement>(null)

  // Fetch dimensions and create lookup map
  const { data: dimensionMap } = useCtxDimensions(dimensionMapSelector)

  // Create allocation method options with translations
  const allocationMethodOptions = useMemo(
    () =>
      Object.values(AllocationMethod).map(value => ({
        value,
        label: t(`allocationMethod:${value}`)
      })),
    [t]
  )

  // Handle cell value changed
  const onCellValueChanged = useCallback(
    (event: CellValueChangedEvent<AllocationBudgetRow>) => {
      const { data, colDef } = event
      if (!data) return

      const { field } = colDef

      // Handle allocation method change
      if (field === 'allocationMethod') {
        onMethodChange(data.id, event.newValue as AllocationMethod)
        // Use redrawRows to force re-evaluation of editable callbacks for dependent columns
        setTimeout(() => {
          gridRef.current?.api?.redrawRows({ rowNodes: [event.node] })
        }, 0)
      }

      // Handle history range change
      if (field === 'allocationOptions.historyRange') {
        onOptionsChange(data.id, {
          ...data.allocationOptions,
          historyRange: event.newValue
        })
      }

      // Handle growth percentage change
      if (field === 'allocationOptions.growthPercentage') {
        onOptionsChange(data.id, {
          ...data.allocationOptions,
          growthPercentage: event.newValue ?? 0
        })
      }
    },
    [onMethodChange, onOptionsChange]
  )

  // Column definitions
  const columnDefs = useMemo<ColDef<AllocationBudgetRow>[]>(
    () => [
      {
        headerName: t('budgetAllocationModal:code'),
        field: 'accountCode',
        width: 80,
        sortable: true,
        hide: rows.length > 0 && rows[0].entityType === 'formula'
      },
      {
        headerName:
          rows.length > 0 && rows[0].entityType === 'formula'
            ? t('entity:budgetDriver')
            : t('budgetAllocationModal:account'),
        field: 'accountName',
        width: 150,
        minWidth: 200,
        flex: 1,
        sortable: true
      },
      {
        headerName: t('budgetAllocationModal:dimension'),
        field: 'dimensionId',
        width: 120,
        minWidth: 150,
        flex: 1,
        sortable: true,
        valueFormatter: params => {
          if (!params.value) return ''
          // Handle root dimension
          if (params.value === 'null') return t('global:enterpriseLevel')
          const dimension = dimensionMap?.get(params.value)
          return dimension?.name ?? params.value
        }
      },
      {
        headerName: t('budgetAllocationModal:dataType', 'Datatyyppi'),
        field: 'dataType',
        type: 'dataType',
        width: 100,
        sortable: true
      },
      {
        headerName: t('budgetAllocationModal:period'),
        field: 'periodLabel',
        width: 150,
        sortable: true
      },
      {
        headerName: t('budgetAllocationModal:value'),
        field: 'value',
        width: 120,
        sortable: true,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: formatGridValue
      },
      {
        headerName: t('budgetAllocationModal:allocationMethod'),
        field: 'allocationMethod',
        type: 'select',
        width: 200,
        sortable: true,
        editable: (params: EditableCallbackParams<AllocationBudgetRow>) => !params.data?.isDeletion,
        cellEditorParams: {
          options: allocationMethodOptions
        }
      },
      {
        headerName: t('budgetAllocationModal:historyPeriod'),
        field: 'allocationOptions.historyRange',
        type: 'dateRange',
        width: 220,
        sortable: false,
        cellRendererParams: {
          dateFormat: 'tableMonth'
        },
        editable: (params: EditableCallbackParams<AllocationBudgetRow>) => {
          return !params.data?.isDeletion && params.data?.allocationMethod === AllocationMethod.WEIGHTED
        },
        valueGetter: (params: ValueGetterParams<AllocationBudgetRow>) => {
          return params.data?.allocationOptions?.historyRange
        },
        valueSetter: (params: ValueSetterParams<AllocationBudgetRow>) => {
          if (params.data) {
            params.data.allocationOptions = {
              ...params.data.allocationOptions,
              historyRange: params.newValue
            }
            return true
          }
          return false
        }
      },
      {
        headerName: t('budgetAllocationModal:growthPercentage'),
        field: 'allocationOptions.growthPercentage',
        type: 'percentage',
        width: 110,
        sortable: false,
        editable: (params: EditableCallbackParams<AllocationBudgetRow>) => {
          // Growth percentage is available for all allocation methods
          return !params.data?.isDeletion
        },
        cellEditorParams: {
          allowNegative: true,
          minValue: -1
        },
        valueSetter: (params: ValueSetterParams<AllocationBudgetRow>) => {
          if (params.data) {
            params.data.allocationOptions = {
              ...params.data.allocationOptions,
              growthPercentage: params.newValue ?? 0
            }
            return true
          }
          return false
        }
      }
    ],
    [allocationMethodOptions, dimensionMap, t, rows]
  )

  const cellSelection = useMemo<CellSelectionOptions>(
    () => ({
      handle: {
        mode: 'fill' as const
      }
    }),
    []
  )

  // Grid options
  const gridOptions = useMemo<GridOptions<AllocationBudgetRow>>(
    () => ({
      popupParent: modalContentRef.current || undefined,
      suppressRowClickSelection: true,
      singleClickEdit: false,
      enableCellTextSelection: false,
      ensureDomOrder: true,
      stopEditingWhenCellsLoseFocus: true,
      rowClassRules: {
        'deleted-budget-row': params => params.data?.isDeletion === true
      },
      getRowStyle: params => {
        if (params.data?.isDeletion) {
          return {
            opacity: 0.6,
            backgroundColor: '#fff1f0'
          }
        }
        return undefined
      }
    }),
    [modalContentRef.current]
  )

  return (
    <div ref={modalContentRef} style={{ height: '100%', width: '100%', overflow: 'auto' }}>
      <CustomAgGrid
        ref={gridRef}
        rowData={rows}
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        cellSelection={cellSelection}
        getRowId={params => params.data.id}
        onCellValueChanged={onCellValueChanged}
        suppressCellFocus={false}
      />
    </div>
  )
}
